import { FC, useEffect, useRef } from "react";
import { gql } from "@apollo/client";
import { useRouter } from "next/router";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { UserActionPermissionBooleans } from "@src/lib/contexts/ActionPermissionsContext";
import { useToast } from "@src/lib/hooks/useToast";
import { GraphqlOperations, UserSelectionInput } from "@src/types.generated";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Skeleton } from "@src/lib/ui/Skeleton";
import useNewMessagingEnabled from "@src/features/messaging/posts/useNewMessagingEnabled";
import MessageBlastDialog from "@src/features/messaging/MessageBlastDialog";
import CreateDirectMessageDialog from "@src/features/messaging/new/chats/CreateDirectMessageDialog";
import SendSeparateChatMessagesDialog from "@src/features/messaging/new/chats/SendSeparateChatMessagesDialog";
import { useCreateDirectMessageMutation } from "@src/features/messaging/new/chats/CreateDirectMessageDialog.generated";
import { isUserOnlySelection } from "@src/features/messaging/new/chats/utils";
import { useMessageDialogGateUsersQuery } from "./MessageDialogGate.generated";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userSelection?: UserSelectionInput;
  onSubmit?: () => void;
};

// Falls back to the legacy sendMessages permission until Ff.NewMessaging is
// fully rolled out; after rollout this is createChat only.
export const resolveCanCreateChats = (
  newMessagingEnabled: boolean,
  permissions: UserActionPermissionBooleans | undefined,
): boolean =>
  newMessagingEnabled ? !!permissions?.createChat : !!permissions?.sendMessages;

export const useCanCreateChats = (): boolean => {
  const newMessagingEnabled = useNewMessagingEnabled();
  const { userActionPermissions } = useActionPermissions();
  return resolveCanCreateChats(newMessagingEnabled, userActionPermissions);
};

export const getGateResolvedUserIds = (
  userSelection: UserSelectionInput,
  resolvedUsers: readonly { id: number }[],
): number[] =>
  isUserOnlySelection(userSelection)
    ? userSelection.userIds ?? []
    : resolvedUsers.map((user) => user.id);

const NewMessagingGate: FC<Props & { userSelection: UserSelectionInput }> = ({
  open,
  onOpenChange,
  userSelection,
  onSubmit,
}) => {
  const router = useRouter();
  const { addToast, addErrorToast } = useToast();
  const createChatFiredRef = useRef(false);
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  const hasExplicitUserIds = isUserOnlySelection(userSelection);
  const { data, error } = useMessageDialogGateUsersQuery({
    variables: { input: userSelection },
    skip: hasExplicitUserIds,
  });
  const userIds = getGateResolvedUserIds(
    userSelection,
    data?.GetUsersForUserSelection ?? [],
  );
  // Keyed off data/error presence rather than `loading`: if the caller swaps
  // the selection mid-lifecycle, the query's skip->active transition can
  // render a frame with loading=false before the fetch starts, which would
  // otherwise read as "no recipients" and close the gate.
  const resolving = !hasExplicitUserIds && !data && !error;
  const singleUserId = !resolving && userIds.length === 1 ? userIds[0] : null;
  const noRecipients = !resolving && userIds.length === 0;

  const [createDirectMessage] = useCreateDirectMessageMutation({
    refetchQueries: [GraphqlOperations.Query.NewMessagingChatSections],
    onError: (error) => {
      if (!openRef.current) return;
      addErrorToast({ ...error, callsite: "create_direct_message" });
      onOpenChange(false);
    },
    onCompleted: (result) => {
      if (!openRef.current) return;
      if (
        !result.createDirectMessage.success ||
        !result.createDirectMessage.chat
      ) {
        addErrorToast({ data: result, callsite: "create_direct_message" });
        onOpenChange(false);
        return;
      }
      onOpenChange(false);
      router.push({
        pathname: "/messages/my-chats/[chatId]",
        query: { chatId: result.createDirectMessage.chat.id },
      });
    },
  });

  useEffect(() => {
    if (!open) {
      createChatFiredRef.current = false;
      return;
    }
    if (singleUserId == null || createChatFiredRef.current) return;
    createChatFiredRef.current = true;
    createDirectMessage({
      variables: { input: { userIds: [singleUserId] } },
    });
  }, [open, singleUserId, createDirectMessage]);

  useEffect(() => {
    if (!open || !noRecipients) return;
    if (error) {
      addErrorToast({ ...error, callsite: "message_dialog_gate_users" });
    } else {
      addToast({ message: "No users to message", success: false });
    }
    onOpenChange(false);
  }, [open, noRecipients, error, addToast, addErrorToast, onOpenChange]);

  if (resolving || singleUserId != null || noRecipients) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-w-[440px] flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Send messages</DialogTitle>
          </DialogHeader>
          <Skeleton className="h-24 w-full" />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <SendSeparateChatMessagesDialog
      open={open}
      onOpenChange={onOpenChange}
      userIds={userIds}
      onSubmit={onSubmit}
    />
  );
};

const MessageDialogGate: FC<Props> = (props) => {
  const newMessagingEnabled = useNewMessagingEnabled();
  if (!newMessagingEnabled) {
    return <MessageBlastDialog {...props} />;
  }
  if (!props.userSelection) {
    // Callers with no selection context (the left-nav Message item) pick
    // recipients and land in a direct message.
    return (
      <CreateDirectMessageDialog
        open={props.open}
        onOpenChange={props.onOpenChange}
      />
    );
  }
  return <NewMessagingGate {...props} userSelection={props.userSelection} />;
};

export default MessageDialogGate;

gql`
  query MessageDialogGateUsers($input: UserSelectionInput!) {
    GetUsersForUserSelection(input: $input) {
      id
    }
  }
`;
